import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, FolderOpen, Trash2, AudioLines, Library, BarChart3, ShieldAlert, Pencil } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { musicApi, errMsg } from "@/lib/musicApi";
import { CreatePanel } from "@/components/music/CreatePanel";
import { TrackCard } from "@/components/music/TrackCard";
import { MusicInsights } from "@/components/music/MusicInsights";

const TABS = [["create", "Create", AudioLines], ["library", "Library", Library], ["insights", "Admin insights", BarChart3]];

// Group versions under their newest take.
function groupTracks(tracks) {
  const by = new Map();
  tracks.forEach((t) => { const k = t.root_id || t.id; by.set(k, [...(by.get(k) || []), t]); });
  return [...by.values()].map((g) => g.sort((a, b) => (b.version || 1) - (a.version || 1)))
    .sort((a, b) => b[0].created_at.localeCompare(a[0].created_at));
}

export default function MusicStudio() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [tab, setTab] = useState("create");
  const isAdmin = user?.role === "admin";

  const loadProjects = useCallback(async () => { const p = await musicApi.projects(); setProjects(p); setPid((c) => c || p[0]?.id); }, []);
  const loadTracks = useCallback(async () => { if (pid) setTracks(await musicApi.tracks(pid)); }, [pid]);
  useEffect(() => {
    if (!isAdmin) return;
    musicApi.config().then(setConfig).catch((e) => toast.error(errMsg(e, "Could not load AI Music Studio")));
    loadProjects().catch(() => {});
  }, [isAdmin, loadProjects]);
  useEffect(() => { loadTracks().catch(() => {}); }, [loadTracks]);
  const live = tracks.some((t) => t.status === "queued" || t.status === "running");
  useEffect(() => {
    if (!live) return undefined;
    const iv = setInterval(() => loadTracks().catch(() => {}), 3000);
    return () => clearInterval(iv);
  }, [live, loadTracks]);
  const groups = useMemo(() => groupTracks(tracks), [tracks]);
  const refresh = () => { loadTracks(); loadProjects(); };

  const newProject = async () => {
    const name = window.prompt("Project name");
    if (!name?.trim()) return;
    try { const p = await musicApi.createProject(name.trim()); await loadProjects(); setPid(p.id); } catch (e) { toast.error(errMsg(e)); }
  };
  const renameProject = async (p) => {
    const name = window.prompt("Rename project", p.name);
    if (!name?.trim()) return;
    try { await musicApi.renameProject(p.id, name.trim()); loadProjects(); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" and all ${p.track_count} tracks? Audio files are removed permanently.`)) return;
    try { await musicApi.deleteProject(p.id); setPid(null); setProjects([]); await loadProjects(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (user && !isAdmin) return <div className="min-h-screen bg-[#070707] text-white"><Navbar /><p className="py-32 text-center text-white/50" data-testid="music-admin-only">AI Music Studio is available to admins only for now.</p></div>;

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid="music-studio-page">
      <Navbar />
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_20%_0%,rgba(200,255,77,0.10),transparent_60%),radial-gradient(ellipse_at_90%_10%,rgba(122,215,255,0.08),transparent_55%)]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-6 px-5 py-8 lg:grid-cols-[240px_1fr]">
        <aside className="space-y-4" data-testid="music-sidebar">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C8FF4D]/30 bg-[#C8FF4D]/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#C8FF4D]">Admin preview</span>
            <h1 className="mt-3 font-display text-3xl font-black leading-none tracking-tight">AI Music<br />Studio</h1>
            <p className="mt-2 text-xs leading-relaxed text-white/45">Original instrumentals, scores and arrangements from text or a reference.</p>
          </div>
          <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.14em] text-white/40"><span>Projects</span>
            <button onClick={newProject} data-testid="music-new-project" className="rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white" aria-label="New project"><Plus size={14} /></button></div>
          <div className="space-y-1">
            {projects.map((p) => (
              <div key={p.id} className={`group flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm ${p.id === pid ? "bg-white/10 text-white" : "text-white/55 hover:bg-white/5"}`}>
                <button onClick={() => setPid(p.id)} data-testid={`music-project-${p.id}`} className="flex min-w-0 flex-1 items-center gap-2 text-left"><FolderOpen size={14} /><span className="truncate">{p.name}</span><span className="ml-auto font-mono text-[10px] text-white/35">{p.track_count}</span></button>
                <button onClick={() => renameProject(p)} data-testid={`music-project-rename-${p.id}`} className="text-white/25 hover:text-white"><Pencil size={12} /></button>
                <button onClick={() => deleteProject(p)} data-testid={`music-project-delete-${p.id}`} className="text-white/25 hover:text-red-400"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-[10.5px] leading-relaxed text-white/40" data-testid="music-rights-note">
            <ShieldAlert size={13} className="mb-1 text-[#FFB547]" />
            Outputs aren't guaranteed copyright-free. You're responsible for publishing or monetizing them under the licences of your inputs, the model and applicable law. No note-for-note copies or vocal removal. <a href="/music-takedown" className="text-[#7AD7FF] hover:underline">Report a track</a>
          </div>
        </aside>
        <main className="min-w-0">
          <div className="mb-6 flex gap-1 border-b border-white/10">
            {TABS.map(([k, l, I]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`music-tab-${k}`}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-bold transition-colors ${tab === k ? "border-[#C8FF4D] text-white" : "border-transparent text-white/45 hover:text-white"}`}><I size={15} /> {l}</button>
            ))}
          </div>
          {!config ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#C8FF4D]" /></div> : tab === "insights" ? (
            <MusicInsights config={config} onSettings={(s) => setConfig({ ...config, settings: s })} />
          ) : tab === "create" ? (
            <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_400px]">
              <CreatePanel config={config} projectId={pid} onCreated={refresh} />
              <div className="space-y-3" data-testid="music-recent">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">Latest in this project</div>
                {groups.slice(0, 5).map((g) => <TrackCard key={g[0].id} t={g[0]} versions={g.slice(1)} onChange={refresh} onNew={refresh} />)}
                {!groups.length && <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-xs text-white/35">Your tracks will appear here.</p>}
              </div>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2" data-testid="music-library">
              {groups.map((g) => <TrackCard key={g[0].id} t={g[0]} versions={g.slice(1)} onChange={refresh} onNew={refresh} />)}
              {!groups.length && <p className="col-span-full py-16 text-center text-sm text-white/40">No tracks in this project yet.</p>}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
